import {
  parseLoomAuthoringDocument,
  reduceLoomAuthoringCommand,
  type LoomAuthoringCommand,
  type LoomAuthoringDocument,
  type LoomFuselageStation,
  type LoomPrimitiveDefinition,
  type LoomSceneNode,
  type LoomWingPanel,
} from "./loomAuthoringModel";

export const LOOM_EXPLICIT_SURFACE_DEFORMATION_SCHEMA =
  "loom-explicit-surface-deformation/v1" as const;

export interface LoomExplicitControlRange {
  readonly maximum: number;
  readonly minimum: number;
}

export interface LoomWingSectionValues {
  readonly centerZM: number;
  readonly chordM: number;
  readonly leadingEdgeXM: number;
  readonly twistDeg: number;
}

export interface LoomFuselagePointValues {
  readonly xM: number;
  readonly zCenterM: number;
}

export interface LoomFuselageSectionValues extends LoomFuselagePointValues {
  readonly heightExponent: number;
  readonly heightM: number;
  readonly widthExponent: number;
  readonly widthM: number;
}

export type LoomExplicitSurfaceHandle =
  | {
      readonly handleId: string;
      readonly kind: "wing-section";
      readonly liveBounds: Readonly<Record<keyof LoomWingSectionValues, LoomExplicitControlRange>>;
      readonly nodeId: string;
      readonly panelId: string;
      readonly section: "root" | "tip";
      readonly values: LoomWingSectionValues;
    }
  | {
      readonly handleId: string;
      readonly kind: "fuselage-point-station";
      readonly liveBounds: Readonly<Record<keyof LoomFuselagePointValues, LoomExplicitControlRange>>;
      readonly nodeId: string;
      readonly stationId: string;
      readonly values: LoomFuselagePointValues;
    }
  | {
      readonly handleId: string;
      readonly kind: "fuselage-section-station";
      readonly liveBounds: Readonly<Record<keyof LoomFuselageSectionValues, LoomExplicitControlRange>>;
      readonly nodeId: string;
      readonly stationId: string;
      readonly values: LoomFuselageSectionValues;
    };

export interface LoomWingSectionTarget {
  readonly centerZM?: number;
  readonly chordM?: number;
  readonly kind: "wing-section";
  readonly leadingEdgeXM?: number;
  readonly panelId: string;
  readonly section: "root" | "tip";
  readonly twistDeg?: number;
}

export interface LoomFuselageStationTarget {
  readonly heightExponent?: number;
  readonly heightM?: number;
  readonly kind: "fuselage-station";
  readonly stationId: string;
  readonly widthExponent?: number;
  readonly widthM?: number;
  readonly xM?: number;
  readonly zCenterM?: number;
}

export type LoomExplicitSurfaceTarget = LoomWingSectionTarget | LoomFuselageStationTarget;

export interface LoomExplicitSurfaceDeformationRequest {
  readonly commandId: string;
  readonly expectedRevision: number;
  readonly handles: readonly LoomExplicitSurfaceTarget[];
  readonly nodeId: string;
  readonly schema: typeof LOOM_EXPLICIT_SURFACE_DEFORMATION_SCHEMA;
}

export const LOOM_EXPLICIT_CONTROL_DERIVATIVE_CONTRACT = Object.freeze({
  controlToDefinition: "analytic-closed-form" as const,
  explicitSurface: "reevaluate-authoritative-parametric-map" as const,
  hybridExtractionJacobian: "not-provided" as const,
  meshVertexAuthoring: false as const,
});

export type LoomExplicitSurfacePreviewContract =
  | {
      readonly kind: "topology-preserving-live";
      readonly scope: "explicit-primitive-control-topology";
      readonly hybridTrim: "stale-exact-rebuild-required";
    }
  | {
      readonly kind: "exact-rebuild-required";
      readonly reason: string;
      readonly hybridTrim: "stale-exact-rebuild-required";
    };

export interface LoomExplicitSurfaceDeformationPlan {
  readonly affectedHandleIds: readonly string[];
  readonly command: Extract<LoomAuthoringCommand, { kind: "primitive.replace" }>;
  readonly derivative: typeof LOOM_EXPLICIT_CONTROL_DERIVATIVE_CONTRACT;
  readonly preview: LoomExplicitSurfacePreviewContract;
  readonly previewDocument: LoomAuthoringDocument;
}

type MutableRecord = Record<string, unknown>;

function record(value: unknown, label: string): MutableRecord {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error(`${label} must be an object`);
  }
  return value as MutableRecord;
}

function exactKeys(source: MutableRecord, allowed: readonly string[], label: string): void {
  const unexpected = Object.keys(source).find((key) => !allowed.includes(key));
  if (unexpected) throw new Error(`${label}.${unexpected} is unsupported`);
}

function text(value: unknown, label: string): string {
  if (typeof value !== "string" || value.trim().length === 0) throw new Error(`${label} must be a non-empty string`);
  return value;
}

function finite(value: unknown, label: string): number {
  if (typeof value !== "number" || !Number.isFinite(value)) throw new Error(`${label} must be finite`);
  return value;
}

function optionalFinite(source: MutableRecord, name: string, label: string): number | undefined {
  return source[name] === undefined ? undefined : finite(source[name], `${label}.${name}`);
}

function immutable<T>(value: T): T {
  if (typeof value !== "object" || value === null || Object.isFrozen(value)) return value;
  Object.values(value).forEach((child) => immutable(child));
  return Object.freeze(value);
}

function range(value: number, radius: number, positive = false): LoomExplicitControlRange {
  return Object.freeze({ minimum: positive ? Math.max(Number.EPSILON, value - radius) : value - radius, maximum: value + radius });
}

function wingValues(panel: LoomWingPanel): { root: LoomWingSectionValues; tip: LoomWingSectionValues } {
  const span = panel.yTipM - panel.yRootM;
  return {
    root: { leadingEdgeXM: panel.xOffsetM, centerZM: panel.zOffsetM, chordM: panel.chordRootM, twistDeg: panel.twistRootDeg },
    tip: {
      leadingEdgeXM: panel.xOffsetM + panel.sweepLocationRatio * (panel.chordRootM - panel.chordTipM)
        + Math.tan(panel.sweepDeg * Math.PI / 180) * span,
      centerZM: panel.zOffsetM + Math.tan(panel.dihedralDeg * Math.PI / 180) * span,
      chordM: panel.chordTipM,
      twistDeg: panel.twistTipDeg,
    },
  };
}

function wingHandles(node: Extract<LoomSceneNode, { type: "primitive" }>): LoomExplicitSurfaceHandle[] {
  if (node.definition.primitive !== "wing") throw new Error(`node ${node.id} is not a wing`);
  if (node.definition.panels.length !== 1) {
    throw new Error("wing section cage v1 requires exactly one panel; joined multi-panel continuity is not implemented");
  }
  const panel = node.definition.panels[0]!;
  const values = wingValues(panel);
  const span = panel.yTipM - panel.yRootM;
  const spatialRadius = Math.max(span * 0.0125, 1e-4);
  return (["root", "tip"] as const).map((section) => ({
    handleId: `node:${node.id}/panel:${panel.panelId}/section:${section}`,
    kind: "wing-section",
    liveBounds: Object.freeze({
      leadingEdgeXM: range(values[section].leadingEdgeXM, spatialRadius),
      centerZM: range(values[section].centerZM, spatialRadius),
      chordM: range(values[section].chordM, values[section].chordM * 0.05, true),
      twistDeg: range(values[section].twistDeg, 0.5),
    }),
    nodeId: node.id,
    panelId: panel.panelId,
    section,
    values: Object.freeze(values[section]),
  }));
}

function fuselageHandles(node: Extract<LoomSceneNode, { type: "primitive" }>): LoomExplicitSurfaceHandle[] {
  if (node.definition.primitive !== "fuselage") throw new Error(`node ${node.id} is not a fuselage`);
  const stations = node.definition.stations;
  const length = stations[stations.length - 1]!.xM - stations[0]!.xM;
  return stations.map((station, stationIndex) => {
    const before = stations[stationIndex - 1];
    const after = stations[stationIndex + 1];
    const spacing = Math.min(before ? station.xM - before.xM : Infinity, after ? after.xM - station.xM : Infinity);
    const xRadius = Math.max((Number.isFinite(spacing) ? spacing : length) * 0.05, 1e-4);
    const base = {
      handleId: `node:${node.id}/station:${station.stationId}`,
      nodeId: node.id,
      stationId: station.stationId,
    };
    if (station.kind === "point") {
      const values = { xM: station.xM, zCenterM: station.zCenterM };
      return immutable({ ...base, kind: "fuselage-point-station", values, liveBounds: {
        xM: range(station.xM, xRadius), zCenterM: range(station.zCenterM, Math.max(length * 0.01, 1e-4)),
      } });
    }
    const values = { xM: station.xM, zCenterM: station.zCenterM, widthM: station.widthM,
      heightM: station.heightM, widthExponent: station.widthExponent, heightExponent: station.heightExponent };
    const sectionScale = Math.max(station.widthM, station.heightM);
    return immutable({ ...base, kind: "fuselage-section-station", values, liveBounds: {
      xM: range(station.xM, xRadius), zCenterM: range(station.zCenterM, sectionScale * 0.05),
      widthM: range(station.widthM, station.widthM * 0.05, true),
      heightM: range(station.heightM, station.heightM * 0.05, true),
      widthExponent: range(station.widthExponent, 0.1, true),
      heightExponent: range(station.heightExponent, 0.1, true),
    } });
  });
}

function primitiveNode(document: LoomAuthoringDocument, nodeId: string): Extract<LoomSceneNode, { type: "primitive" }> {
  const node = document.nodes.find((candidate) => candidate.id === nodeId);
  if (!node || node.type !== "primitive") throw new Error(`node ${nodeId} is not an explicit primitive`);
  if (node.definition.primitive !== "wing" && node.definition.primitive !== "fuselage") {
    throw new Error(`node ${nodeId} has no explicit control cage`);
  }
  return node;
}

export function deriveLoomExplicitSurfaceHandles(documentValue: unknown, nodeId: string): readonly LoomExplicitSurfaceHandle[] {
  const document = parseLoomAuthoringDocument(documentValue);
  const node = primitiveNode(document, nodeId);
  return immutable(node.definition.primitive === "wing" ? wingHandles(node) : fuselageHandles(node));
}

function parseRequest(value: unknown): LoomExplicitSurfaceDeformationRequest {
  const source = record(value, "explicit surface deformation request");
  exactKeys(source, ["schema", "commandId", "expectedRevision", "handles", "nodeId"], "request");
  if (source.schema !== LOOM_EXPLICIT_SURFACE_DEFORMATION_SCHEMA) throw new Error("unsupported explicit surface deformation schema");
  const expectedRevision = finite(source.expectedRevision, "request.expectedRevision");
  if (!Number.isSafeInteger(expectedRevision) || expectedRevision < 0) throw new Error("request.expectedRevision must be a non-negative integer");
  if (!Array.isArray(source.handles) || source.handles.length === 0 || source.handles.length > 32) {
    throw new Error("request.handles must contain between 1 and 32 handles");
  }
  const handles = source.handles.map((raw, index): LoomExplicitSurfaceTarget => {
    const item = record(raw, `request.handles[${index}]`);
    if (item.kind === "wing-section") {
      exactKeys(item, ["kind", "panelId", "section", "leadingEdgeXM", "centerZM", "chordM", "twistDeg"], `request.handles[${index}]`);
      const section = item.section;
      if (section !== "root" && section !== "tip") throw new Error(`request.handles[${index}].section is unsupported`);
      const target: LoomWingSectionTarget = { kind: "wing-section", panelId: text(item.panelId, `request.handles[${index}].panelId`), section,
        leadingEdgeXM: optionalFinite(item, "leadingEdgeXM", `request.handles[${index}]`), centerZM: optionalFinite(item, "centerZM", `request.handles[${index}]`),
        chordM: optionalFinite(item, "chordM", `request.handles[${index}]`), twistDeg: optionalFinite(item, "twistDeg", `request.handles[${index}]`) };
      if ([target.leadingEdgeXM, target.centerZM, target.chordM, target.twistDeg].every((entry) => entry === undefined)) throw new Error(`request.handles[${index}] has no target coordinates`);
      if (target.chordM !== undefined && target.chordM <= 0) throw new Error(`request.handles[${index}].chordM must be positive`);
      return target;
    }
    if (item.kind !== "fuselage-station") throw new Error(`request.handles[${index}].kind is unsupported`);
    exactKeys(item, ["kind", "stationId", "xM", "zCenterM", "widthM", "heightM", "widthExponent", "heightExponent"], `request.handles[${index}]`);
    const target: LoomFuselageStationTarget = { kind: "fuselage-station", stationId: text(item.stationId, `request.handles[${index}].stationId`),
      xM: optionalFinite(item, "xM", `request.handles[${index}]`), zCenterM: optionalFinite(item, "zCenterM", `request.handles[${index}]`),
      widthM: optionalFinite(item, "widthM", `request.handles[${index}]`), heightM: optionalFinite(item, "heightM", `request.handles[${index}]`),
      widthExponent: optionalFinite(item, "widthExponent", `request.handles[${index}]`), heightExponent: optionalFinite(item, "heightExponent", `request.handles[${index}]`) };
    if ([target.xM, target.zCenterM, target.widthM, target.heightM, target.widthExponent, target.heightExponent].every((entry) => entry === undefined)) throw new Error(`request.handles[${index}] has no target coordinates`);
    for (const [name, entry] of [["widthM", target.widthM], ["heightM", target.heightM], ["widthExponent", target.widthExponent], ["heightExponent", target.heightExponent]] as const) {
      if (entry !== undefined && entry <= 0) throw new Error(`request.handles[${index}].${name} must be positive`);
    }
    return target;
  });
  return { schema: LOOM_EXPLICIT_SURFACE_DEFORMATION_SCHEMA, commandId: text(source.commandId, "request.commandId"),
    expectedRevision, nodeId: text(source.nodeId, "request.nodeId"), handles };
}

function within(value: number, bound: LoomExplicitControlRange): boolean {
  return value >= bound.minimum - 1e-12 && value <= bound.maximum + 1e-12;
}

function wingDefinition(node: Extract<LoomSceneNode, { type: "primitive" }>, targets: readonly LoomWingSectionTarget[], handles: readonly LoomExplicitSurfaceHandle[]): { definition: LoomPrimitiveDefinition; local: boolean; changed: boolean } {
  if (node.definition.primitive !== "wing") throw new Error("wing handles require a wing node");
  const panel = node.definition.panels[0]!;
  const descriptors = handles.filter((handle): handle is Extract<LoomExplicitSurfaceHandle, { kind: "wing-section" }> => handle.kind === "wing-section");
  const current = wingValues(panel);
  const values = { root: { ...current.root }, tip: { ...current.tip } };
  let local = true;
  let changed = false;
  for (const target of targets) {
    if (target.panelId !== panel.panelId) throw new Error(`wing panel ${target.panelId} does not exist`);
    const descriptor = descriptors.find((handle) => handle.section === target.section)!;
    for (const name of ["leadingEdgeXM", "centerZM", "chordM", "twistDeg"] as const) {
      const next = target[name];
      if (next === undefined) continue;
      changed ||= next !== values[target.section][name];
      values[target.section][name] = next;
      local &&= within(next, descriptor.liveBounds[name]);
    }
  }
  const span = panel.yTipM - panel.yRootM;
  const sweepDeg = Math.atan((values.tip.leadingEdgeXM - values.root.leadingEdgeXM
    - panel.sweepLocationRatio * (values.root.chordM - values.tip.chordM)) / span) * 180 / Math.PI;
  const dihedralDeg = Math.atan((values.tip.centerZM - values.root.centerZM) / span) * 180 / Math.PI;
  if (Math.abs(sweepDeg) >= 80 || Math.abs(dihedralDeg) >= 80) throw new Error("section cage would create an unsupported near-singular wing angle");
  const updated: LoomWingPanel = { ...panel, xOffsetM: values.root.leadingEdgeXM, zOffsetM: values.root.centerZM,
    chordRootM: values.root.chordM, chordTipM: values.tip.chordM, twistRootDeg: values.root.twistDeg,
    twistTipDeg: values.tip.twistDeg, sweepDeg, dihedralDeg };
  return { definition: { ...node.definition, panels: [updated] }, local, changed };
}

function fuselageDefinition(node: Extract<LoomSceneNode, { type: "primitive" }>, targets: readonly LoomFuselageStationTarget[], handles: readonly LoomExplicitSurfaceHandle[]): { definition: LoomPrimitiveDefinition; local: boolean; changed: boolean } {
  if (node.definition.primitive !== "fuselage") throw new Error("fuselage handles require a fuselage node");
  const byId = new Map(targets.map((target) => [target.stationId, target]));
  let local = true;
  let changed = false;
  const stations = node.definition.stations.map((station): LoomFuselageStation => {
    const target = byId.get(station.stationId);
    if (!target) return station;
    const descriptor = handles.find((handle) => "stationId" in handle && handle.stationId === station.stationId)!;
    if (station.kind === "point" && [target.widthM, target.heightM, target.widthExponent, target.heightExponent].some((entry) => entry !== undefined)) {
      throw new Error(`point station ${station.stationId} has no section size or exponent controls`);
    }
    const result = { ...station } as MutableRecord;
    for (const name of ["xM", "zCenterM", "widthM", "heightM", "widthExponent", "heightExponent"] as const) {
      const next = target[name];
      if (next === undefined) continue;
      if (!(name in descriptor.values) || !(name in descriptor.liveBounds)) throw new Error(`station ${station.stationId} has no ${name} control`);
      changed ||= next !== result[name];
      result[name] = next;
      local &&= within(next, descriptor.liveBounds[name as keyof typeof descriptor.liveBounds] as LoomExplicitControlRange);
    }
    return result as unknown as LoomFuselageStation;
  });
  for (const target of targets) if (!stations.some((station) => station.stationId === target.stationId)) throw new Error(`fuselage station ${target.stationId} does not exist`);
  for (let index = 1; index < stations.length; index += 1) if (!(stations[index]!.xM > stations[index - 1]!.xM)) throw new Error("station cage must preserve strict axial station order");
  return { definition: { ...node.definition, stations }, local, changed };
}

export function compileLoomExplicitSurfaceDeformation(documentValue: unknown, requestValue: unknown): LoomExplicitSurfaceDeformationPlan {
  const document = parseLoomAuthoringDocument(documentValue);
  const request = parseRequest(requestValue);
  if (request.expectedRevision !== document.revision) throw new Error(`revision conflict: expected ${request.expectedRevision}, current ${document.revision}`);
  const node = primitiveNode(document, request.nodeId);
  const keys = request.handles.map((handle) => handle.kind === "wing-section" ? `${handle.panelId}:${handle.section}` : handle.stationId);
  if (new Set(keys).size !== keys.length) throw new Error("explicit surface deformation contains duplicate handles");
  const handles = deriveLoomExplicitSurfaceHandles(document, node.id);
  const result = node.definition.primitive === "wing"
    ? wingDefinition(node, request.handles.map((handle) => {
        if (handle.kind !== "wing-section") throw new Error("wing deformation accepts only wing section handles");
        return handle;
      }), handles)
    : fuselageDefinition(node, request.handles.map((handle) => {
        if (handle.kind !== "fuselage-station") throw new Error("fuselage deformation accepts only station handles");
        return handle;
      }), handles);
  if (!result.changed) throw new Error("explicit surface deformation must change at least one control coordinate");
  const command = immutable({ schema: "loom-authoring-command/v1", commandId: request.commandId,
    expectedRevision: request.expectedRevision, kind: "primitive.replace", nodeId: node.id, definition: result.definition } as const);
  const reduction = reduceLoomAuthoringCommand(document, command);
  const affectedHandleIds = immutable(request.handles.map((target) => target.kind === "wing-section"
    ? `node:${node.id}/panel:${target.panelId}/section:${target.section}` : `node:${node.id}/station:${target.stationId}`));
  const preview: LoomExplicitSurfacePreviewContract = result.local
    ? immutable({ kind: "topology-preserving-live", scope: "explicit-primitive-control-topology", hybridTrim: "stale-exact-rebuild-required" })
    : immutable({ kind: "exact-rebuild-required", reason: "edit exceeds the local explicit-control preview bounds", hybridTrim: "stale-exact-rebuild-required" });
  return immutable({ affectedHandleIds, command, derivative: LOOM_EXPLICIT_CONTROL_DERIVATIVE_CONTRACT,
    preview, previewDocument: reduction.document });
}
